"use client";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Modal, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as godModeActions from "../../redux/godMode/reducer";
import "./style.css";

const { fetchGodModeCenters, startGodModeSession } = godModeActions;

const SEARCH_DEBOUNCE_MS = 350;

/**
 * God Mode center picker.
 *
 * Rendered on the login profile screen, but only for accounts the server has
 * confirmed are on the operator allowlist — `isOperator` comes from
 * /godMode/status, never from anything the client decides for itself.
 *
 * Every entry requires a reason before the session opens, because the audit
 * trail is the thing that makes this feature defensible.
 */
const GodModeCenterPicker: React.FC = () => {
    const dispatch = useDispatch();
    const { isOperator, centers, centersLoading, isLoading } = useSelector(
        (state: any) => state.godMode || {},
    );

    const [search, setSearch] = useState("");
    const [selectedCenter, setSelectedCenter] = useState<any>(null);
    const [reason, setReason] = useState("");
    // Open by default: the panel only renders for operators, and browsing clients
    // is the reason they are on this screen.
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        if (!isOperator || !expanded) return undefined;
        const timer = setTimeout(() => {
            dispatch(fetchGodModeCenters({ search }));
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [isOperator, expanded, search]);

    const visibleCenters = useMemo(() => centers || [], [centers]);

    if (!isOperator) return null;

    const onConfirm = () => {
        if (!selectedCenter || reason.trim().length < 5) return;
        dispatch(
            startGodModeSession({
                centerId: selectedCenter.centerId,
                reason: reason.trim(),
            }),
        );
        setSelectedCenter(null);
        setReason("");
    };

    return (
        <div className="god-mode-picker">
            <div className="god-mode-picker__header">
                <div>
                    <p className="god-mode-picker__title">God Mode</p>
                    <p className="god-mode-picker__subtitle">
                        Open any client account. Sessions start read-only and are recorded.
                    </p>
                </div>
                <Button type="link" onClick={() => setExpanded((value) => !value)}>
                    {expanded ? "Hide" : "Browse clients"}
                </Button>
            </div>

            {expanded && (
                <>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Search by company name, center id or admin email"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className="god-mode-picker__search"
                    />

                    <div className="god-mode-picker__list">
                        {centersLoading ? (
                            <div className="god-mode-picker__loading">
                                <Spin />
                            </div>
                        ) : visibleCenters.length === 0 ? (
                            <Empty description="No centers match that search" />
                        ) : (
                            visibleCenters.map((center: any) => (
                                <div className="god-mode-picker__row" key={center.centerId}>
                                    <div className="god-mode-picker__row-info">
                                        <span className="god-mode-picker__row-name">
                                            {center.schoolName || center.centerId}
                                        </span>
                                        <span className="god-mode-picker__row-meta">
                                            {center.centerId}
                                            {center.adminEmail ? ` · ${center.adminEmail}` : ""}
                                            {center.deactivate ? " · deactivated" : ""}
                                        </span>
                                    </div>
                                    <Button
                                        size="small"
                                        disabled={center.deactivate}
                                        onClick={() => setSelectedCenter(center)}
                                    >
                                        Enter
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            <Modal
                title={`Enter ${selectedCenter?.schoolName || selectedCenter?.centerId || ""}`}
                open={!!selectedCenter}
                onCancel={() => setSelectedCenter(null)}
                onOk={onConfirm}
                okText="Start read-only session"
                confirmLoading={isLoading}
                okButtonProps={{ disabled: reason.trim().length < 5 }}
            >
                <p>
                    You will enter this client&apos;s account as a support identity. You will not
                    appear in their staff list, and the session is read-only until you take
                    control.
                </p>
                <Input.TextArea
                    rows={3}
                    placeholder="Why are you entering this account? (e.g. ticket #1423 — bookings not showing)"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    maxLength={500}
                />
            </Modal>
        </div>
    );
};

export default GodModeCenterPicker;
